import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import type { ObjectKind, TaskDetail, Template } from '../api/types.js'
import { KIND_LABEL, KIND_PLURAL, ObjectPicker, childKinds } from '../components/objects.js'
import { AssigneePicker, PersonPicker, type Assignment } from '../components/people.js'
import { PhotoCompare } from '../components/PhotoCompare.js'
import { History } from '../components/History.js'
import { useToast } from '../components/toast.js'
import { Field, Modal, Notice, Spinner, StatusBadge } from '../components/ui.js'
import { formatDate, formatDateTime, todayIso } from '../lib/format.js'

type Mode = 'view' | 'record' | 'final' | 'callback'

const EACH_KIND: Record<ObjectKind, string> = {
  customer: 'Jeder Kunde',
  site: 'Jede Liegenschaft',
  building: 'Jedes Gebäude',
  unit: 'Jede Einheit',
  component: 'Jedes Bauteil',
}

/** Done with "Kein Zugang" or a Mangel: somebody has to call the worker back. */
export function needsCallback(task: Pick<TaskDetail, 'status' | 'defectNote' | 'textOption' | 'callbackOptions'>) {
  return task.status === 'done' && Boolean(task.defectNote || (task.textOption && task.callbackOptions?.includes(task.textOption)))
}

/**
 * "Rückruf geklärt" (Workshop 23.10.): what came out of the call, and when the
 * flat needs another visit, the date for it.
 */
export function CallbackForm({ taskId, onCancel, onDone }: { taskId: string; onCancel: () => void; onDone: (message: string) => void }) {
  const [note, setNote] = useState('')
  const [again, setAgain] = useState(false)
  const [date, setDate] = useState(todayIso())
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () =>
      api<{ followUpId: string | null }>(`/api/occurrences/${taskId}/callback`, {
        method: 'POST',
        body: { note: note.trim() || undefined, followUpDate: again ? date : undefined },
      }),
    onSuccess: (result) => onDone(result.followUpId ? `Rückruf geklärt, neuer Termin am ${formatDate(date)} angelegt.` : 'Rückruf geklärt.'),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })
  return (
    <form
      className="stack"
      style={{ gap: 8 }}
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      {error && <Notice kind="error">{error}</Notice>}
      <Field label="Was wurde geklärt? (freiwillig)">
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Schlüssel liegt beim Hausmeister" autoFocus />
      </Field>
      <label className="check">
        <input type="checkbox" checked={again} onChange={(e) => setAgain(e.target.checked)} />
        Neuer Termin für diese Wohnung
      </label>
      {again && (
        <Field label="Neuer Termin">
          <input type="date" value={date} min={todayIso()} onChange={(e) => e.target.value && setDate(e.target.value)} />
        </Field>
      )}
      <div className="actions">
        <button type="submit" className="btn small" disabled={save.isPending}>
          Geklärt
        </button>
        <button type="button" className="btn ghost small" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  )
}

export function TaskModal({
  taskId,
  onClose,
  onChanged,
}: {
  taskId: string | null
  onClose: () => void
  onChanged: (message?: string) => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>('view')
  const [performedById, setPerformedById] = useState('')
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data: task } = useQuery({
    queryKey: ['occurrence', taskId],
    queryFn: () => api<TaskDetail>(`/api/occurrences/${taskId}`),
    enabled: Boolean(taskId),
  })
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/occurrences/${taskId}`, { method: 'PATCH', body }),
    onSuccess: () => onChanged('Aufgabe aktualisiert.'),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  /** Marking a task as cancelled is undoable, so it needs no confirmation. */
  const skip = useMutation({
    mutationFn: () => api(`/api/occurrences/${taskId}`, { method: 'PATCH', body: { status: 'skipped' } }),
    onSuccess: () => {
      toast('Aufgabe entfällt, sie zählt nicht in die Erfüllungsquote.', {
        action: {
          label: 'Rückgängig',
          onAction: () =>
            api(`/api/occurrences/${taskId}`, { method: 'PATCH', body: { status: 'open' } }).then(() =>
              onChanged('Aufgabe wieder offen.'),
            ),
        },
      })
      onChanged()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  /**
   * "Endgültig nicht erledigt": the deliberate opposite of a Nachtrag. The
   * task leaves every list, is never paid and never reported to Youtilly, and
   * stays in the proof with the reason. Reopening is one click.
   */
  const finalMissed = useMutation({
    mutationFn: (reason: string) => api(`/api/occurrences/${taskId}/final-missed`, { method: 'POST', body: { reason: reason.trim() || undefined } }),
    onSuccess: () => {
      toast('Endgültig geschlossen, wird weder vergütet noch an Youtilly gemeldet.', {
        action: {
          label: 'Rückgängig',
          onAction: () =>
            api(`/api/occurrences/${taskId}`, { method: 'PATCH', body: { status: 'open' } }).then(() =>
              onChanged('Aufgabe wieder offen.'),
            ),
        },
      })
      setMode('view')
      onChanged()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  /** Only offered for ad-hoc tasks, planned ones belong to the record. */
  const remove = useMutation({
    mutationFn: () => api(`/api/occurrences/${taskId}`, { method: 'DELETE' }),
    onSuccess: () => onChanged('Sonderaufgabe gelöscht.'),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Löschen fehlgeschlagen.'),
  })

  const record = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/occurrences/${taskId}/record`, { method: 'POST', body }),
    onSuccess: () => {
      setMode('view')
      onChanged('Erledigung nachgetragen, sie ist im Nachweis als Nachtrag gekennzeichnet.')
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const reopenCallback = useMutation({
    mutationFn: () => api(`/api/occurrences/${taskId}/callback`, { method: 'POST', body: { reopen: true } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['occurrence', taskId] })
      onChanged('Rückruf wieder offen.')
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const textOptions = task?.textOptions ?? []
  const closed = task?.status === 'done' || task?.status === 'final_missed'
  const signaturePhoto = task?.photos.find((photo) => photo.kind === 'signature')

  return (
    <Modal
      open={taskId !== null}
      title={task?.title ?? 'Aufgabe'}
      onClose={() => {
        setMode('view')
        setError(null)
        onClose()
      }}
    >
      {!task ? (
        <Spinner />
      ) : (
        <>
          {error && <Notice kind="error">{error}</Notice>}

          <div className="spread" style={{ marginBottom: 14 }}>
            <div style={{ minWidth: 0 }}>
              <div className="strong">
                {task.objectKind === 'customer' ? (
                  <span>
                    {task.street}
                    {task.zip || task.city ? `, ${[task.zip, task.city].filter(Boolean).join(' ')}` : ''}
                  </span>
                ) : (
                  <Link to={`/objekte/${task.siteId ?? task.objectId}`}>{task.siteName}</Link>
                )}
              </div>
              <div className="muted">
                {task.objectKind === 'customer' ? (
                  <>
                    ohne Gebäude, beim Kunden <Link to={`/objekte/${task.objectId}`} style={{ color: 'inherit' }}>{task.customerName}</Link>
                  </>
                ) : task.path.length > 0 ? (
                  <Link to={`/objekte/${task.objectId}`} style={{ color: 'inherit' }}>
                    {task.path.join(' › ')}
                  </Link>
                ) : (
                  'gesamte Liegenschaft'
                )}
              </div>
            </div>
            <StatusBadge status={task.status} />
          </div>

          <table className="data" style={{ marginBottom: 16 }}>
            <tbody>
              <tr>
                <td className="muted">Fällig</td>
                <td className="strong">
                  {formatDate(task.dueDate)}
                  {task.dueTime ? `, ${task.dueTime} Uhr` : ''}
                </td>
              </tr>
              {task.unitLabel && (
                <tr>
                  <td className="muted">Wohnung</td>
                  <td className="strong">{task.unitLabel}</td>
                </tr>
              )}
              <tr>
                <td className="muted">Zuständig</td>
                <td>
                  {task.assigneeId ? (
                    <Link to={`/mitarbeiter/${task.assigneeId}`}>{task.assigneeName}</Link>
                  ) : task.teamName ? (
                    <Link to="/mitarbeiter?ansicht=gruppen">Gruppe {task.teamName}</Link>
                  ) : (
                    '–'
                  )}
                  {task.teamName && task.status === 'done' && task.performedByName && (
                    <span className="muted small">, erledigt von {task.performedByName}</span>
                  )}
                </td>
              </tr>
              {task.planId && (task.planAssigneeId ?? null) !== (task.assigneeId ?? null) && (
                <tr>
                  <td className="muted">Vertretung für</td>
                  <td>
                    {task.planAssigneeId ? (
                      <Link to={`/mitarbeiter/${task.planAssigneeId}`}>{task.planAssigneeName}</Link>
                    ) : task.planTeamName ? (
                      <Link to="/mitarbeiter?ansicht=gruppen">Gruppe {task.planTeamName}</Link>
                    ) : (
                      <span className="muted">Plan ohne Zuständigen</span>
                    )}
                    <span className="muted small"> (laut Plan zuständig)</span>
                  </td>
                </tr>
              )}
              <tr>
                <td className="muted">Herkunft</td>
                <td>
                  {task.planId ? (
                    <Link to={`/plaene?plan=${task.planId}&zurueck=1`}>aus Aufgabenplan, Plan öffnen</Link>
                  ) : (
                    <span>Sonderaufgabe</span>
                  )}
                  {task.templateId && (
                    <>
                      {' '}
                      <span className="muted">|</span> <Link to={`/vorlagen?bearbeiten=${task.templateId}`}>Leistung</Link>
                    </>
                  )}
                </td>
              </tr>
              {task.externalRef && (
                <tr>
                  <td className="muted">Youtilly-ID</td>
                  <td>
                    {task.externalRef}
                    {task.reportedAt ? (
                      <span className="badge done" style={{ marginLeft: 8 }}>
                        gemeldet {formatDateTime(task.reportedAt)}
                      </span>
                    ) : task.reportError ? (
                      <span className="badge missed" style={{ marginLeft: 8 }} title={task.reportError}>
                        Meldung fehlgeschlagen
                      </span>
                    ) : task.status === 'done' ? (
                      <span className="badge" style={{ marginLeft: 8 }}>
                        noch nicht gemeldet
                      </span>
                    ) : null}
                  </td>
                </tr>
              )}
              {task.status === 'done' && (
                <>
                  {task.textOption && (
                    <tr>
                      <td className="muted">Ergebnis</td>
                      <td className="strong">{task.textOption}</td>
                    </tr>
                  )}
                  {task.measurementValue !== null && task.measurementValue !== undefined && (
                    <tr>
                      <td className="muted">{task.measurementLabel ?? 'Messwert'}</td>
                      <td className="strong">
                        {formatMeasurement(task.measurementValue)}
                        {task.measurementUnit ? ` ${task.measurementUnit}` : ''}
                      </td>
                    </tr>
                  )}
                  {signaturePhoto && (
                    <tr>
                      <td className="muted">Unterschrift</td>
                      <td>
                        {task.signedBy ?? <span className="muted">ohne Namen</span>}
                        <a href={`/api/photos/${signaturePhoto.id}`} target="_blank" rel="noreferrer">
                          <img className="signature-thumb" src={`/api/photos/${signaturePhoto.id}/thumb`} alt="Unterschrift" />
                        </a>
                      </td>
                    </tr>
                  )}
                  {task.completedLat !== null && task.completedLat !== undefined && task.completedLng !== null && task.completedLng !== undefined && (
                    <tr>
                      <td className="muted">Standort</td>
                      <td>
                        <a
                          href={`https://www.openstreetmap.org/?mlat=${task.completedLat}&mlon=${task.completedLng}#map=18/${task.completedLat}/${task.completedLng}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          auf der Karte zeigen
                        </a>
                        {task.completedAccuracy !== null && task.completedAccuracy !== undefined && (
                          <span className="muted small">, genau auf etwa {task.completedAccuracy} m</span>
                        )}
                        <div className="muted small">beim Abhaken einmal mitgeschickt, mit Einwilligung</div>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="muted">Erledigt</td>
                    <td>
                      {formatDateTime(task.completedAtClient)}
                      {task.performedByName && <span className="muted">, {task.performedByName}</span>}
                    </td>
                  </tr>
                  <tr>
                    <td className="muted">Erfasst</td>
                    <td>
                      {task.completionSource === 'backoffice' ? (
                        <span className="badge warn">von der Verwaltung nachgetragen</span>
                      ) : (
                        <span className="badge done">in der App</span>
                      )}
                      {task.backofficeReason && <div className="muted small">{task.backofficeReason}</div>}
                    </td>
                  </tr>
                  {task.completedAtServer &&
                    task.completedAtClient &&
                    Date.parse(task.completedAtServer) - Date.parse(task.completedAtClient) > 5 * 60_000 && (
                      <tr>
                        <td className="muted">Eingegangen</td>
                        <td>
                          {formatDateTime(task.completedAtServer)}
                          <div className="muted small">später übertragen, das Gerät war offline</div>
                        </td>
                      </tr>
                    )}
                  {task.exportedAt && (
                    <tr>
                      <td className="muted">Vergütung</td>
                      <td className="small">exportiert am {formatDateTime(task.exportedAt)}</td>
                    </tr>
                  )}
                </>
              )}
              {task.status === 'final_missed' && (
                <tr>
                  <td className="muted">Grund</td>
                  <td>
                    {task.finalReason}
                    <div className="muted small">Wird nicht vergütet und nicht an Youtilly gemeldet.</div>
                  </td>
                </tr>
              )}
              {task.note && (
                <tr>
                  <td className="muted">Notiz</td>
                  <td>{task.note}</td>
                </tr>
              )}
              {task.defectNote && (
                <tr>
                  <td className="muted">Mangelmeldung</td>
                  <td className="text-bad">{task.defectNote}</td>
                </tr>
              )}
              {needsCallback(task) && (
                <tr>
                  <td className="muted">Rückruf</td>
                  <td>
                    {task.callbackResolvedAt ? (
                      <>
                        <span className="badge done">geklärt</span>{' '}
                        <span className="small">
                          {formatDateTime(task.callbackResolvedAt)}
                          {task.callbackResolvedByName ? `, ${task.callbackResolvedByName}` : ''}
                        </span>
                        {task.callbackNote && <div>{task.callbackNote}</div>}
                        <button type="button" className="link-button small" onClick={() => reopenCallback.mutate()}>
                          wieder öffnen
                        </button>
                      </>
                    ) : mode === 'callback' ? (
                      <CallbackForm
                        taskId={task.id}
                        onCancel={() => setMode('view')}
                        onDone={(message) => {
                          setMode('view')
                          void queryClient.invalidateQueries({ queryKey: ['occurrence', taskId] })
                          onChanged(message)
                        }}
                      />
                    ) : (
                      <>
                        <span className="badge warn">offen</span>{' '}
                        <span className="small muted">Mitarbeiter anrufen und klären, z. B. Schlüssel oder neuer Termin.</span>
                        <div style={{ marginTop: 6 }}>
                          <button type="button" className="btn small" onClick={() => setMode('callback')}>
                            Geklärt
                          </button>
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <TaskPhotos photos={task.photos.filter((photo) => photo.kind !== 'signature')} />
          {task.status === 'done' && task.photos.some((photo) => photo.kind !== 'signature') && <QualityBox task={task} onChanged={() => void queryClient.invalidateQueries({ queryKey: ['occurrence', taskId] })} />}

          {!closed && mode === 'view' && (
            <>
              <div className="row">
                <Field label="Zuständig">
                  <AssigneePicker
                    value={{ assigneeId: task.assigneeId, assigneeTeamId: task.assigneeTeamId }}
                    onChange={(value) => patch.mutate({ ...value })}
                  />
                </Field>
                <Field label="Fällig am">
                  <input type="date" defaultValue={task.dueDate} onChange={(e) => e.target.value && patch.mutate({ dueDate: e.target.value })} />
                </Field>
              </div>
              <div className="row">
                <Field label="Hinweis für den Mitarbeiter" hint="Wird beim Verlassen des Feldes gespeichert.">
                  <input
                    defaultValue={task.note ?? ''}
                    onBlur={(e) => {
                      if ((e.target.value || null) !== (task.note ?? null)) patch.mutate({ note: e.target.value || '' })
                    }}
                  />
                </Field>
                <Field label="Youtilly-ID">
                  <input
                    defaultValue={task.externalRef ?? ''}
                    onBlur={(e) => {
                      if ((e.target.value || null) !== (task.externalRef ?? null)) patch.mutate({ externalRef: e.target.value || null })
                    }}
                  />
                </Field>
              </div>
            </>
          )}

          {!closed && mode === 'view' && (
            <div className="row" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setPerformedById(task.assigneeId ?? '')
                  setMode('record')
                }}
              >
                Sofort erledigen
              </button>
              <button type="button" className="btn secondary" onClick={() => setMode('final')}>
                Endgültig nicht erledigt
              </button>
              <button type="button" className="btn ghost" onClick={() => skip.mutate()}>
                Entfällt
              </button>
              {task.isAdhoc && (
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => {
                    if (confirm(`Sonderaufgabe „${task.title}“ wirklich löschen?`)) remove.mutate()
                  }}
                >
                  Löschen
                </button>
              )}
            </div>
          )}

          {task.status === 'final_missed' && mode === 'view' && (
            <div className="row" style={{ marginTop: 12 }}>
              <button type="button" className="btn secondary" onClick={() => patch.mutate({ status: 'open' })}>
                Wieder öffnen
              </button>
            </div>
          )}

          {mode === 'record' && (
            <form
              style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line-soft)' }}
              onSubmit={(event: FormEvent<HTMLFormElement>) => {
                event.preventDefault()
                const form = new FormData(event.currentTarget)
                record.mutate({
                  reason: form.get('reason'),
                  performedById: performedById || null,
                  textOption: form.get('textOption') || undefined,
                  note: form.get('note') || undefined,
                  measurementValue: task.templateMeasurementLabel
                    ? Number(String(form.get('measurementValue') ?? '').replace(',', '.'))
                    : undefined,
                })
              }}
            >
              <Notice kind="warn">
                Nachträge sind erlaubt, werden im Nachweis aber ausdrücklich als solche gekennzeichnet, mit Grund.
              </Notice>
              <Field label="Wer hat die Arbeit ausgeführt?">
                <PersonPicker value={performedById} onChange={(id) => setPerformedById(id)} emptyLabel="Unbekannt" includeInactive />
              </Field>
              {textOptions.length > 0 && (
                <Field label="Was wurde gemacht?" hint="Pflichtangabe, danach richtet sich die Abrechnung.">
                  <select name="textOption" required defaultValue="">
                    <option value="">Bitte wählen …</option>
                    {textOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {task.templateMeasurementLabel && (
                <Field label={`${task.templateMeasurementLabel}${task.templateMeasurementUnit ? ` in ${task.templateMeasurementUnit}` : ''}`}>
                  <input name="measurementValue" required inputMode="decimal" pattern="-?[0-9]+([,.][0-9]+)?" />
                </Field>
              )}
              <Field label="Grund für den Nachtrag" hint="z. B. „Handy-Akku leer“, „telefonisch bestätigt“">
                <input name="reason" required autoFocus />
              </Field>
              <Field
                label="Notiz"
                hint={(task.reasonOptions ?? []).length > 0 ? `Pflicht bei: ${(task.reasonOptions ?? []).join(', ')}` : undefined}
              >
                <textarea name="note" />
              </Field>
              <div className="row">
                <button type="submit" className="btn" disabled={record.isPending}>
                  Nachtragen
                </button>
                <button type="button" className="btn secondary" onClick={() => setMode('view')}>
                  Abbrechen
                </button>
              </div>
            </form>
          )}

          {mode === 'final' && (
            <form
              style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line-soft)' }}
              onSubmit={(event: FormEvent<HTMLFormElement>) => {
                event.preventDefault()
                const form = new FormData(event.currentTarget)
                finalMissed.mutate(String(form.get('reason') ?? ''))
              }}
            >
              <Notice kind="warn">
                Die Aufgabe verschwindet aus allen Listen und bleibt im Nachweis als „endgültig nicht erledigt“ stehen. Sie wird nicht
                vergütet und nicht an Youtilly gemeldet.
              </Notice>
              <Field label="Grund (freiwillig)" hint="Erscheint im Nachweis.">
                <input name="reason" autoFocus maxLength={500} placeholder="z. B. Woche vorbei, nicht mehr nachholbar" />
              </Field>
              <div className="row">
                <button type="submit" className="btn danger" disabled={finalMissed.isPending}>
                  Endgültig schließen
                </button>
                <button type="button" className="btn secondary" onClick={() => setMode('view')}>
                  Abbrechen
                </button>
              </div>
            </form>
          )}

          {mode === 'view' && (
            <div className="task-extras">
              {closed && (
                <a className="btn secondary small" href={`/api/occurrences/${task.id}/proof`} target="_blank" rel="noreferrer">
                  Einzelnachweis (PDF)
                </a>
              )}
              <details style={{ marginTop: 10 }}>
                <summary>Verlauf</summary>
                <History entity="task_occurrence" entityId={task.id} />
              </details>
            </div>
          )}
        </>
      )}
    </Modal>
  )
}

function formatMeasurement(value: number): string {
  return value.toLocaleString('de-DE', { maximumFractionDigits: 2 })
}

export function AdhocModal({
  open,
  defaultObjectId,
  onClose,
  onCreated,
}: {
  open: boolean
  defaultObjectId?: string
  onClose: () => void
  onCreated: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [objectId, setObjectId] = useState(defaultObjectId ?? '')
  const [templateId, setTemplateId] = useState('')
  const [assignment, setAssignment] = useState<Assignment>({ assigneeId: null, assigneeTeamId: null })
  const [objectKind, setObjectKind] = useState<ObjectKind | null>(null)
  const [spreadKind, setSpreadKind] = useState<ObjectKind | ''>('')
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/api/templates') })
  const toast = useToast()
  // A customer carries no tasks itself, so choosing one means "for each … below".
  const spreadOptions = objectKind ? childKinds(objectKind).filter((k) => k !== 'customer') : []

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<{ count?: number }>('/api/occurrences', { method: 'POST', body }),
    onSuccess: (result) => {
      if (result?.count) toast(`${result.count} Aufgaben angelegt.`)
      setObjectId(defaultObjectId ?? '')
      setObjectKind(null)
      setSpreadKind('')
      setTemplateId('')
      setAssignment({ assigneeId: null, assigneeTeamId: null })
      onCreated()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const template = templates.data?.find((t) => t.id === templateId)

  return (
    <Modal
      open={open}
      title="Sonderaufgabe anlegen"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form="adhoc-form" className="btn" disabled={create.isPending}>
            Anlegen
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <p className="small muted" style={{ marginTop: 0 }}>
        Für alles, was nicht nach Plan läuft, Winterdienst nach Schneefall, Sturmschaden, ein Auftrag aus Youtilly.
      </p>
      <form
        id="adhoc-form"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          if (!objectId) {
            setError('Bitte ein Objekt wählen.')
            return
          }
          if (objectKind === 'customer' && !spreadKind) {
            setError('Beim Kunden bitte wählen, für welche Objekte darunter die Aufgabe gilt.')
            return
          }
          create.mutate({
            objectId,
            spreadKind: spreadKind || null,
            templateId: templateId || null,
            title: form.get('title') || undefined,
            ...assignment,
            dueDate: form.get('dueDate'),
            note: form.get('note') || undefined,
            photoRequirement: templateId ? undefined : form.get('photoRequirement'),
            externalRef: form.get('externalRef') || undefined,
          })
        }}
      >
        <Field label="Wo?" hint="Ein Objekt, oder ein Kunde bzw. eine Liegenschaft für viele Objekte auf einmal.">
          <ObjectPicker
            value={objectId}
            taskable={false}
            onChange={(id, node) => {
              setObjectId(id)
              setObjectKind(node?.kind ?? null)
              setSpreadKind(node?.kind === 'customer' ? 'site' : '')
            }}
            autoFocus={!defaultObjectId}
          />
        </Field>
        {spreadOptions.length > 0 && (
          <Field
            label="Gilt für"
            hint={spreadKind ? 'Je Objekt entsteht eine eigene Aufgabe, z. B. Winterdienst für jede Liegenschaft.' : undefined}
          >
            <select value={spreadKind} onChange={(e) => setSpreadKind(e.target.value as ObjectKind | '')}>
              {objectKind !== 'customer' && <option value="">Nur dieses Objekt ({KIND_LABEL[objectKind!]})</option>}
              {spreadOptions.map((k) => (
                <option key={k} value={k}>
                  {EACH_KIND[k]} darunter ({KIND_PLURAL[k]})
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Leistung" hint="Aus einer Vorlage, dann gelten deren Fotopflicht und Textbausteine.">
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">Freie Bezeichnung</option>
            {templates.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        {!templateId && (
          <Field label="Bezeichnung">
            <input name="title" required placeholder="z. B. Winterdienst Gehweg" />
          </Field>
        )}
        {template && template.textOptions.length > 0 && (
          <div className="chips" style={{ marginBottom: 14 }}>
            {template.textOptions.map((o) => (
              <span key={o} className="chip">
                {o}
              </span>
            ))}
          </div>
        )}
        <div className="row">
          <Field label="Datum">
            <input name="dueDate" type="date" required defaultValue={todayIso()} />
          </Field>
          <Field label="Zuständig">
            <AssigneePicker value={assignment} onChange={setAssignment} />
          </Field>
        </div>
        <div className="row">
          {!templateId && (
            <Field label="Fotopflicht">
              <select name="photoRequirement" defaultValue="after">
                <option value="none">Kein Foto</option>
                <option value="after">Foto nachher</option>
                <option value="before_after">Foto vorher und nachher</option>
              </select>
            </Field>
          )}
          <Field label="Youtilly-ID" hint="Wenn der Auftrag vom Kunden kommt.">
            <input name="externalRef" />
          </Field>
        </div>
        <Field label="Hinweis für den Mitarbeiter">
          <textarea name="note" placeholder="z. B. Schneefall über Nacht, Gehwege bis 7 Uhr räumen." />
        </Field>
      </form>
    </Modal>
  )
}

/**
 * A before/after pair is shown as a wipe comparison; anything else falls back to
 * plain thumbnails.
 */
const PHOTO_KIND_LABEL: Record<string, string> = { before: 'vorher', after: 'nachher', other: 'sonstiges', issue: 'Mangel', inspection: 'Begehung' }

function TaskPhotos({ photos }: { photos: TaskDetail['photos'] }) {
  if (photos.length === 0) return null

  const before = photos.find((photo) => photo.kind === 'before')
  const after = photos.find((photo) => photo.kind === 'after')
  const rest = photos.filter((photo) => photo !== before && photo !== after)

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="strong small" style={{ marginBottom: 8 }}>
        Fotos ({photos.length})
      </div>

      {before && after ? (
        <PhotoCompare beforeId={before.id} afterId={after.id} />
      ) : (
        <div className="photo-grid">
          {[before, after].filter(Boolean).map((photo) => (
            <a key={photo!.id} href={`/api/photos/${photo!.id}`} target="_blank" rel="noreferrer">
              <img src={`/api/photos/${photo!.id}/thumb`} alt={photo!.kind === 'before' ? 'Vorher' : 'Nachher'} />
            </a>
          ))}
        </div>
      )}

      {rest.length > 0 && (
        <div className="photo-grid" style={{ marginTop: 10 }}>
          {rest.map((photo) => (
            <a key={photo.id} className="photo-tile" href={`/api/photos/${photo.id}`} target="_blank" rel="noreferrer">
              <img src={`/api/photos/${photo.id}/thumb`} alt={PHOTO_KIND_LABEL[photo.kind] ?? 'Foto'} />
              <span>{PHOTO_KIND_LABEL[photo.kind] ?? 'Foto'}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

const FLAG_LABEL: Record<string, string> = {
  dunkel: 'Foto sehr dunkel',
  doppelt: 'Gleiches Foto schon bei einer anderen Aufgabe',
  vorher_nachher_gleich: 'Vorher und nachher fast identisch',
  alt: 'Foto lange vor der Erledigung aufgenommen',
  passt_nicht: 'Foto passt nicht zur Leistung',
  nicht_sauber: 'Ergebnis wirkt nicht sauber',
  unklar: 'Auf dem Foto ist wenig zu erkennen',
}

/**
 * The photo check for one task, labelled for what it is: a suggestion. The
 * Verwaltung confirms, dismisses or runs it again; nothing here is said
 * about the person.
 */
function QualityBox({ task, onChanged }: { task: TaskDetail; onChanged: () => void }) {
  const toast = useToast()
  const check = useMutation({
    mutationFn: () => api(`/api/occurrences/${task.id}/quality-check`, { method: 'POST' }),
    onSuccess: onChanged,
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Prüfung fehlgeschlagen.', 'error'),
  })
  const review = useMutation({
    mutationFn: (status: 'bestaetigt' | 'verworfen') => api(`/api/occurrences/${task.id}/quality-review`, { method: 'POST', body: { status } }),
    onSuccess: onChanged,
  })
  const ai = task.qualitySource && task.qualitySource !== 'lokal'
  return (
    <div className="quality-box">
      <div className="spread">
        <span className="strong small">
          Fotoprüfung{' '}
          {task.qualityCheckedAt ? (
            <span className="badge open" style={{ marginLeft: 4 }}>
              {ai ? 'KI-Vorschlag' : 'lokal geprüft'}
            </span>
          ) : null}
        </span>
        <button type="button" className="btn ghost small" onClick={() => check.mutate()} disabled={check.isPending}>
          {check.isPending ? 'prüft …' : task.qualityCheckedAt ? 'Erneut prüfen' : 'Jetzt prüfen'}
        </button>
      </div>
      {task.qualityCheckedAt ? (
        <>
          {(task.qualityFlags ?? []).length > 0 && (
            <div className="chips" style={{ margin: '6px 0' }}>
              {(task.qualityFlags ?? []).map((flag) => (
                <span key={flag} className="chip warn">
                  {FLAG_LABEL[flag] ?? flag}
                </span>
              ))}
            </div>
          )}
          <div className="small">
            {task.qualityScore !== null && <strong>Note {task.qualityScore} von 5. </strong>}
            {task.qualityNote}
          </div>
          {task.qualityStatus === 'vorschlag' ? (
            <div className="actions" style={{ marginTop: 8 }}>
              <button type="button" className="btn secondary small" onClick={() => review.mutate('bestaetigt')}>
                Hinweis bestätigen
              </button>
              <button type="button" className="btn ghost small" onClick={() => review.mutate('verworfen')}>
                Verwerfen
              </button>
            </div>
          ) : (
            <div className="muted small" style={{ marginTop: 4 }}>
              {task.qualityStatus === 'bestaetigt' ? 'Hinweis bestätigt.' : task.qualityStatus === 'verworfen' ? 'Hinweis verworfen.' : 'Unauffällig.'}
            </div>
          )}
        </>
      ) : (
        <div className="muted small">Noch nicht geprüft.</div>
      )}
    </div>
  )
}
